import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Copy, Save, RefreshCw, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageHeader } from '@/components/PageHeader';
import { aiGenerate, getAIToolSettings, addCustomCategory, addCustomChannel } from '@/lib/api';
import { toast } from 'sonner';

// Erweiterte Kategorien
const DEFAULT_CATEGORIES = [
  { value: 'fintech', label: 'Fintech / Banking' },
  { value: 'saas', label: 'SaaS / B2B Software' },
  { value: 'health', label: 'Health & Fitness' },
  { value: 'gaming', label: 'Gaming' },
  { value: 'ecommerce', label: 'E-Commerce / Retail' },
  { value: 'travel', label: 'Travel & Hospitality' },
  { value: 'food', label: 'Food & Delivery' },
  { value: 'social', label: 'Social / Dating' },
  { value: 'education', label: 'Education / E-Learning' },
  { value: 'productivity', label: 'Productivity / Utilities' },
  { value: 'entertainment', label: 'Entertainment / Media' },
  { value: 'real_estate', label: 'Real Estate / Immobilien' },
];

// Erweiterte Kanäle inkl. LinkedIn
const DEFAULT_CHANNELS = [
  { value: 'meta', label: 'Meta (Facebook/Instagram)' },
  { value: 'google', label: 'Google App Campaigns' },
  { value: 'tiktok', label: 'TikTok Ads' },
  { value: 'apple_search_ads', label: 'Apple Search Ads' },
  { value: 'linkedin', label: 'LinkedIn Ads' },
  { value: 'snapchat', label: 'Snapchat Ads' },
  { value: 'twitter', label: 'X (Twitter) Ads' },
  { value: 'pinterest', label: 'Pinterest Ads' },
  { value: 'youtube', label: 'YouTube Ads' },
  { value: 'reddit', label: 'Reddit Ads' },
  { value: 'programmatic', label: 'Programmatic / DSP' },
  { value: 'influencer', label: 'Influencer Marketing' },
];

const TONES = [
  { value: 'professionell', label: 'Professionell' },
  { value: 'modern', label: 'Modern' },
  { value: 'verspielt', label: 'Verspielt' },
  { value: 'vertrauenswuerdig', label: 'Vertrauenswürdig' },
  { value: 'innovativ', label: 'Innovativ' },
  { value: 'freundlich', label: 'Freundlich' },
  { value: 'luxurioes', label: 'Luxuriös / Premium' },
  { value: 'jung', label: 'Jung / Gen-Z' },
];

const TYPES = [
  { value: 'ad_copy', label: 'Anzeigen-Copy' },
  { value: 'aso', label: 'ASO (App Store)' },
  { value: 'creative_brief', label: 'Creative Brief' },
];

const fadeIn = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

// Combobox-artige Select mit "Andere hinzufügen" Option - mit Backend-Integration
const EditableSelect = ({ value, onChange, options, label, placeholder, type, onAddSuccess }) => {
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customValue, setCustomValue] = useState('');
  
  const handleAddCustom = async () => {
    if (customValue.trim()) {
      const newValue = customValue.toLowerCase().replace(/\s+/g, '_');
      const newLabel = customValue.trim();
      
      try {
        // Save to backend
        if (type === 'category') {
          await addCustomCategory(newValue, newLabel);
        } else if (type === 'channel') {
          await addCustomChannel(newValue, newLabel);
        }
        
        // Notify parent to reload
        onAddSuccess({ value: newValue, label: newLabel });
        onChange(newValue);
        setCustomValue('');
        setShowCustomInput(false);
        toast.success(`"${newLabel}" hinzugefügt und gespeichert`);
      } catch (err) {
        console.error('Failed to add custom option:', err);
        toast.error('Fehler beim Speichern');
      }
    }
  };
  
  return (
    <div>
      <label className="text-sm font-medium text-slate-700 mb-1.5 block">{label}</label>
      {showCustomInput ? (
        <div className="flex gap-2">
          <Input 
            value={customValue} 
            onChange={e => setCustomValue(e.target.value)}
            placeholder={placeholder}
            className="flex-1"
            onKeyDown={e => e.key === 'Enter' && handleAddCustom()}
            autoFocus
          />
          <Button size="sm" onClick={handleAddCustom} className="px-3" data-testid="confirm-add-custom">
            <Plus className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowCustomInput(false)} className="px-3">
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <Select value={value} onValueChange={v => {
          if (v === '__add_new__') {
            setShowCustomInput(true);
          } else {
            onChange(v);
          }
        }}>
          <SelectTrigger data-testid={`select-${type}`}>
            <SelectValue placeholder="Auswählen..." />
          </SelectTrigger>
          <SelectContent>
            {options.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
            <SelectItem value="__add_new__" className="text-teal-600 font-medium">
              <span className="flex items-center gap-1">
                <Plus className="w-3 h-3" /> Andere hinzufügen...
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
  );
};

export default function AdminAIToolsPage() {
  const [form, setForm] = useState({
    app_name: '', category: 'fintech', main_features: '', target_channel: 'meta',
    target_audience: '', tone: 'professionell', generate_type: 'ad_copy'
  });
  const [result, setResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  
  // Custom options state (loaded from backend)
  const [customCategories, setCustomCategories] = useState([]);
  const [customChannels, setCustomChannels] = useState([]);
  
  // Load custom settings on mount
  useEffect(() => {
    loadCustomSettings();
  }, []);
  
  const loadCustomSettings = async () => {
    try {
      const response = await getAIToolSettings();
      setCustomCategories(response.data.custom_categories || []);
      setCustomChannels(response.data.custom_channels || []);
    } catch (err) {
      console.error('Failed to load AI tool settings:', err);
    }
  };
  
  const handleCustomAdd = (type, newOption) => {
    if (type === 'category') {
      setCustomCategories(prev => [...prev, newOption]);
    } else if (type === 'channel') {
      setCustomChannels(prev => [...prev, newOption]);
    }
  };

  const handleGenerate = async () => {
    if (!form.app_name) { toast.error('Bitte geben Sie den App-Namen ein.'); return; }
    setGenerating(true);
    try {
      const res = await aiGenerate(form);
      setResult(res.data.result);
      toast.success('Inhalte generiert!');
    } catch (err) {
      toast.error('Generierung fehlgeschlagen. Bitte erneut versuchen.');
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(typeof text === 'string' ? text : JSON.stringify(text, null, 2));
    toast.success('In Zwischenablage kopiert');
  };

  const renderAdCopyResult = () => {
    if (!result) return null;
    return (
      <div className="space-y-4">
        {result.headlines && (
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Headlines</h4>
            {result.headlines.map((h, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg mb-1">
                <span className="text-sm text-slate-800">{h}</span>
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(h)}><Copy className="w-3 h-3" /></Button>
              </div>
            ))}
          </div>
        )}
        {result.primary_text && (
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Haupttext</h4>
            <div className="px-3 py-2 bg-slate-50 rounded-lg flex items-start justify-between">
              <p className="text-sm text-slate-800">{result.primary_text}</p>
              <Button variant="ghost" size="sm" onClick={() => copyToClipboard(result.primary_text)}><Copy className="w-3 h-3" /></Button>
            </div>
          </div>
        )}
        {result.cta && (
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2">CTA</h4>
            <div className="px-3 py-2 bg-teal-50 rounded-lg flex items-center justify-between">
              <span className="text-sm font-medium text-teal-800">{result.cta}</span>
              <Button variant="ghost" size="sm" onClick={() => copyToClipboard(result.cta)}><Copy className="w-3 h-3" /></Button>
            </div>
          </div>
        )}
        {result.aso_description && (
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2">ASO Beschreibung</h4>
            <div className="px-3 py-2 bg-slate-50 rounded-lg flex items-start justify-between">
              <p className="text-sm text-slate-800">{result.aso_description}</p>
              <Button variant="ghost" size="sm" onClick={() => copyToClipboard(result.aso_description)}><Copy className="w-3 h-3" /></Button>
            </div>
          </div>
        )}
        {result.variants && (
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Varianten</h4>
            {result.variants.map((v, i) => (
              <div key={i} className="px-3 py-2 bg-slate-50 rounded-lg mb-2">
                <p className="text-sm font-medium text-slate-900">{v.headline}</p>
                <p className="text-sm text-slate-600">{v.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderASOResult = () => {
    if (!result) return null;
    return (
      <div className="space-y-4">
        {result.title_suggestions && (
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Titel-Vorschläge</h4>
            {result.title_suggestions.map((t, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg mb-1">
                <span className="text-sm text-slate-800">{t}</span>
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(t)}><Copy className="w-3 h-3" /></Button>
              </div>
            ))}
          </div>
        )}
        {result.keywords && (
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Keywords</h4>
            <div className="flex flex-wrap gap-2">
              {result.keywords.map((k, i) => (
                <span key={i} className="text-xs font-medium px-3 py-1 rounded-full bg-sky-50 text-sky-700 border border-sky-100 cursor-pointer hover:bg-sky-100" onClick={() => copyToClipboard(k)}>{k}</span>
              ))}
            </div>
          </div>
        )}
        {result.short_description && (
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Kurzbeschreibung</h4>
            <div className="px-3 py-2 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-800">{result.short_description}</p>
            </div>
          </div>
        )}
        {result.long_description && (
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Langbeschreibung</h4>
            <div className="px-3 py-2 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-800 whitespace-pre-line">{result.long_description}</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCreativeBriefResult = () => {
    if (!result) return null;
    return (
      <div className="space-y-4">
        {Object.entries(result).map(([key, value]) => (
          <div key={key}>
            <h4 className="text-sm font-semibold text-slate-900 mb-1 capitalize">{key.replace(/_/g, ' ')}</h4>
            <div className="px-3 py-2 bg-slate-50 rounded-lg">
              {Array.isArray(value) ? (
                <ul className="list-disc list-inside text-sm text-slate-800 space-y-1">
                  {value.map((v, i) => <li key={i}>{v}</li>)}
                </ul>
              ) : (
                <p className="text-sm text-slate-800 whitespace-pre-line">{value}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }}>
      <PageHeader title="AI-Tools" description="KI-gestützte Inhaltserstellung für Ihre Kampagnen" />

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: Inputs */}
        <motion.div variants={fadeIn} className="rounded-xl border border-slate-200 bg-white p-6">
          <h3 className="font-display text-base font-semibold text-slate-900 mb-4">Eingaben</h3>
          <div className="space-y-4">
            <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Typ</label>
              <Select value={form.generate_type} onValueChange={v => { setForm({...form, generate_type: v}); setResult(null); }}>
                <SelectTrigger data-testid="ai-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">App Name *</label>
              <Input value={form.app_name} onChange={e => setForm({...form, app_name: e.target.value})} placeholder="z.B. SmartBank Pro" data-testid="ai-app-name-input" />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              {/* Kategorie mit "Andere hinzufügen" */}
              <EditableSelect
                label="Kategorie"
                value={form.category}
                onChange={v => setForm({...form, category: v})}
                options={[...DEFAULT_CATEGORIES, ...customCategories]}
                placeholder="Neue Kategorie..."
                type="category"
                onAddSuccess={(newOption) => handleCustomAdd('category', newOption)}
              />
              
              {/* Kanal mit "Andere hinzufügen" */}
              <EditableSelect
                label="Kanal"
                value={form.target_channel}
                onChange={v => setForm({...form, target_channel: v})}
                options={[...DEFAULT_CHANNELS, ...customChannels]}
                placeholder="Neuer Kanal..."
                type="channel"
                onAddSuccess={(newOption) => handleCustomAdd('channel', newOption)}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Hauptfeatures</label>
              <Textarea value={form.main_features} onChange={e => setForm({...form, main_features: e.target.value})} placeholder="Die wichtigsten Features Ihrer App..." rows={3} />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Zielgruppe</label>
              <Input value={form.target_audience} onChange={e => setForm({...form, target_audience: e.target.value})} placeholder="z.B. 25-40 Jahre, technikaffin, B2B Entscheider" />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Ton</label>
              <Select value={form.tone} onValueChange={v => setForm({...form, tone: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TONES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            
            <Button onClick={handleGenerate} disabled={generating} className="w-full btn-lift bg-slate-900 text-white hover:bg-slate-800" data-testid="ai-copy-generate-button">
              {generating ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Wird generiert...</> : <><Sparkles className="w-4 h-4 mr-2" /> Generieren</>}
            </Button>
          </div>
        </motion.div>

        {/* Right: Outputs */}
        <motion.div variants={fadeIn} className="rounded-xl border border-slate-200 bg-white p-6" data-testid="ai-copy-output-panel">
          <h3 className="font-display text-base font-semibold text-slate-900 mb-4">Ergebnis</h3>
          {!result ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Sparkles className="w-10 h-10 text-slate-300 mb-3" />
              <p className="text-sm text-slate-500">Wählen Sie Ihre Einstellungen und klicken Sie auf „Generieren".</p>
            </div>
          ) : (
            <Tabs defaultValue="result" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="result">Ergebnis</TabsTrigger>
                <TabsTrigger value="raw">Raw JSON</TabsTrigger>
              </TabsList>
              <TabsContent value="result" className="max-h-[500px] overflow-y-auto">
                {form.generate_type === 'ad_copy' && renderAdCopyResult()}
                {form.generate_type === 'aso' && renderASOResult()}
                {form.generate_type === 'creative_brief' && renderCreativeBriefResult()}
              </TabsContent>
              <TabsContent value="raw">
                <pre className="text-xs bg-slate-50 p-4 rounded-lg overflow-auto max-h-[500px]">
                  {JSON.stringify(result, null, 2)}
                </pre>
              </TabsContent>
            </Tabs>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}
